"use client";

import { motion, useInView, type Variants } from "framer-motion";
import { ArrowRight } from "lucide-react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRef, useState } from "react";
import { ProductCard } from "@/components/products/product-card";
import { Button } from "@/components/ui/button";
import type { Product } from "@/lib/shopify/types";

const ProductDetailDialog = dynamic(
	() =>
		import("@/components/products/product-detail-dialog").then(
			(m) => m.ProductDetailDialog,
		),
	{ ssr: false },
);

const PRODUCT_BADGES = [
	"High-protein",
	"Chef-crafted",
	"Best-seller",
	"New favorite",
] as const;

const SECTION_CONTENT = {
	tagline: "IAMFAM favorites",
	title: "Start with crowd-pleasers.",
	description:
		"Three best-sellers people reorder on repeat. High protein, clean ingredients, ready when you are.",
	ctaText: "View all products",
} as const;

interface FeaturedProductsProps {
	products: Product[];
}

const fadeIn: Variants = {
	hidden: { opacity: 0, y: 30 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: 0.6, ease: "easeOut" },
	},
};

const staggerContainer: Variants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
			delayChildren: 0.1,
		},
	},
};

export function FeaturedProducts({ products }: FeaturedProductsProps) {
	const sectionRef = useRef<HTMLElement>(null);
	const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
	const [detailProduct, setDetailProduct] = useState<Product | null>(null);

	if (products.length === 0) {
		return null;
	}

	return (
		<>
			<section
				ref={sectionRef}
				className="py-20 md:py-32 bg-white dark:bg-black text-black dark:text-white"
			>
				<div className="container mx-auto px-4">
					<motion.div
						initial="hidden"
						animate={isInView ? "visible" : "hidden"}
						variants={fadeIn}
						className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
					>
						<div>
							<p className="text-sm font-bold text-primary uppercase tracking-wider mb-2">
								{SECTION_CONTENT.tagline}
							</p>
							<h2 className="text-4xl md:text-5xl font-black mt-2 text-balance lg:leading-tight">
								{SECTION_CONTENT.title}
							</h2>
							<p className="text-muted-foreground mt-4 max-w-lg text-lg">
								{SECTION_CONTENT.description}
							</p>
						</div>
						<Button
							variant="outline"
							size="lg"
							className="gap-2 whitespace-nowrap rounded-full px-8 border-2 font-bold hover:bg-primary hover:text-white hover:border-primary transition-all"
							asChild
						>
							<Link href="/build-a-box">
								{SECTION_CONTENT.ctaText}
								<ArrowRight className="h-4 w-4" />
							</Link>
						</Button>
					</motion.div>

					<motion.div
						variants={staggerContainer}
						initial="hidden"
						animate={isInView ? "visible" : "hidden"}
						className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
					>
						{products.slice(0, 4).map((product, index) => (
							<motion.div key={product.id} variants={fadeIn}>
								<ProductCard
									product={product}
									badge={PRODUCT_BADGES[index % PRODUCT_BADGES.length]}
									className="h-full shadow-lg hover:shadow-xl transition-all"
									onProductClick={(p) => setDetailProduct(p)}
								/>
							</motion.div>
						))}
					</motion.div>
				</div>
			</section>

			<ProductDetailDialog
				product={detailProduct}
				onClose={() => setDetailProduct(null)}
			/>
		</>
	);
}
